import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import {
  RevenueChartProps,
  PieData,
} from "@/types/pos/revenue-report";
import { capitalizeFirstLetter } from "@/lib/helper";

const formatChartTooltip = (value: number) => {
  return Number(value).toLocaleString();
};

const CustomLegend = ({ data }: { data: PieData[] }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex flex-col gap-2 h-full max-h-[300px] custom-scrollbar overflow-y-auto pr-2 mt-4">
      {data.map((item, index) => {
        const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
        return (
          <div key={index} className="flex items-center gap-2 text-sm shrink-0">
            <div
              className="w-4 h-4 rounded shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{capitalizeFirstLetter(item.name)}</div>
              <div className="text-xs text-muted-foreground">
                {formatChartTooltip(item.value)} ({percent}%)
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#4dd0e1",
  "#9575cd",
  "#ff8a65",
  "#4db6ac",
  "#f06292",
  "#a1887f",
  "#90a4ae",
  "#dce775",
  "#e57373",
  "#64b5f6",
  "#81c784",
  "#ba68c8",
  "#ffb74d",
  "#4fc3f7",
  "#aed581",
  "#ff8a80",
  "#7986cb",
  "#ff7043",
  "#26a69a",
  "#ab47bc",
  "#5c6bc0",
  "#ef5350",
  "#66bb6a",
  "#ffa726",
  "#29b6f6",
  "#ec407a",
  "#8d6e63",
  "#78909c"
];

export default function RevenueChart({ data, isLoading, filters }: RevenueChartProps) {
  const isFacilityOnlyFilter = useMemo(() => {
    return (
      filters?.facility_ids &&
      filters?.facility_ids.length > 0 &&
      !filters?.item_type &&
      !filters?.item_id
    );
  }, [filters]);

  const isItemTypeFilter = useMemo(() => {
    return !!filters?.item_type;
  }, [filters]);

  const isNoFilterView = useMemo(() => {
    return (
      (!filters?.facility_ids || filters.facility_ids.length === 0) &&
      !filters?.item_type &&
      !filters?.item_id
    );
  }, [filters]);

  const eventVsFacilityData = useMemo(() => {
    if (!data?.total || !isFacilityOnlyFilter) return [];
    return data.total.map((item, index) => ({
      name: item.item_type,
      value: Number(item.total_amount),
      color: COLORS[index % COLORS.length],
    }));
  }, [data, isFacilityOnlyFilter]);

  // Data for Chart 1 in Item Type & Name view OR Default View (Total breakdown)
  const totalDistributionData = useMemo(() => {
    if (!data?.total || (!isItemTypeFilter && !isNoFilterView)) return [];
    return data.total.map((item, index) => ({
      name: item.item_type,
      value: Number(item.total_amount),
      color: COLORS[index % COLORS.length],
    }));
  }, [data, isItemTypeFilter, isNoFilterView]);

  // Data for Chart 2 in Item Type view (Specific type breakdown)
  const specificItemDistributionData = useMemo(() => {
    const type = filters?.item_type;
    if (!type || !data?.[type] || !isItemTypeFilter) return [];
    
    const specificData = data[type];
    if (!Array.isArray(specificData)) return [];

    return specificData.map((item: any, index: number) => ({
      name: item.description || item.item_name || `Item ${item.item_id}`,
      value: Number(item.total_amount),
      color: COLORS[index % COLORS.length],
    }));
  }, [data, filters?.item_type, isItemTypeFilter]);

  const itemTypeData = useMemo(() => {
    if (!data?.item_type_distribution || isFacilityOnlyFilter || isItemTypeFilter || isNoFilterView) return [];
    return data.item_type_distribution.map((item, index) => ({
      name: item.name,
      value: Number(item.value),
      color: COLORS[index % COLORS.length],
    }));
  }, [data, isFacilityOnlyFilter, isItemTypeFilter, isNoFilterView]);

  const paymentMethodData = useMemo(() => {
    if (!data?.payment_method_distribution || isFacilityOnlyFilter || isItemTypeFilter || isNoFilterView) return [];
    return data.payment_method_distribution.map((item, index) => ({
      name: item.name,
      value: Number(item.value),
      color: COLORS[index % COLORS.length],
    }));
  }, [data, isFacilityOnlyFilter, isItemTypeFilter, isNoFilterView]);

  if (isLoading) {
    return (
      <div className="w-full h-80 flex items-center justify-center bg-white rounded-lg border">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const noData = !data || (
    (!isFacilityOnlyFilter && !isItemTypeFilter && !isNoFilterView && !itemTypeData.length && !paymentMethodData.length) ||
    (isFacilityOnlyFilter && !eventVsFacilityData.length) ||
    (isItemTypeFilter && !totalDistributionData.length) ||
    (isNoFilterView && !totalDistributionData.length)
  );

  if (noData) {
    return (
      <div className="w-full h-80 flex items-center justify-center bg-white rounded-lg border">
        <p className="text-gray-500">No data available for the selected filters</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-semibold text-sm">{capitalizeFirstLetter(payload[0].name)}</p>
          <p className="text-primary text-sm">
            Amount: {Number(payload[0].value).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  // View for No advanced filter applied (Default summary)
  if (isNoFilterView) {
    return (
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
            Total Distribution
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 h-[350px]">
            <div className="w-full md:w-1/2 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={totalDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    paddingAngle={1}
                    dataKey="value"
                  >
                    {totalDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full md:w-1/2">
              <CustomLegend data={totalDistributionData} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // View for Facility Only filter
  if (isFacilityOnlyFilter) {
    return (
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
            Event vs Facility Distribution
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 h-[350px]">
            <div className="w-full md:w-1/2 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={eventVsFacilityData}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    paddingAngle={1}
                    dataKey="value"
                  >
                    {eventVsFacilityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full md:w-1/2">
              <CustomLegend data={eventVsFacilityData} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // View for both Item Type and Item Name (item_id) filters
  if (isItemTypeFilter) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Total Distribution Chart */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
            Total Distribution
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="w-full md:w-1/2 h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={totalDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    paddingAngle={1}
                    dataKey="value"
                  >
                    {totalDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full md:w-1/2">
              <CustomLegend data={totalDistributionData} />
            </div>
          </div>
        </div>

        {/* Specific Item Type Distribution Chart */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left capitalize">
            {filters?.item_type?.replace("_", " ")} Distribution
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="w-full md:w-1/2 h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={specificItemDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    paddingAngle={1}
                    dataKey="value"
                  >
                    {specificItemDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full md:w-1/2">
              <CustomLegend data={specificItemDistributionData} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default view
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Item Type Distribution Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
          Item Type Distribution
        </h3>
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="w-full md:w-1/2 h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={itemTypeData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {itemTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full md:w-1/2">
            <CustomLegend data={itemTypeData} />
          </div>
        </div>
      </div>

      {/* Payment Method Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold mb-6 text-gray-800 text-left">
          Payment Method Distribution
        </h3>
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="w-full md:w-1/2 h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full md:w-1/2">
            <CustomLegend data={paymentMethodData} />
          </div>
        </div>
      </div>
    </div>
  );
}


